import {EventEmitter, Injectable, NgZone, OnDestroy, Output} from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {apiUrl, webSocketUrl} from "../../environments/environment";
import {Frame} from "stompjs";
import {Observable, Subject, takeUntil} from "rxjs";
import SockJS from "sockjs-client";
import * as Stomp from "stompjs";
import {User} from "../_models/user";
import {AuthService} from "./auth.service";
import {GlobalStore} from "../global.store";
import {CHANGES_SAVED_MESSAGE} from "../config/constants";
import {Notification as NotificationModel} from "../_models/notification";
import {TargetCompanyService} from "./target-company.service";
import {UsersService} from "./users.service";

@Injectable({
  providedIn: 'root'
})
export class NotificationService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private unreadCountRequestVersion = 0;
  @Output() newConnectionStatus: EventEmitter<boolean> = new EventEmitter<boolean>();
  readonly notificationsChanged$ = new Subject<void>();

  showNotification: EventEmitter<any> = new EventEmitter<any>();
  notificationsList: NotificationModel[] = [];
  unreadNotificationsList: NotificationModel[] = [];
  socket: WebSocket;
  stompClient: any;
  unreadNotifCount: number = 0
  reconnectDelay: number = 3;
  reconnectAttempt: number = 0;
  currentCompanyId: number | null = null;
  isConnectingWebsocket = false;

  pingInterval: any;
  pingTimeout: any;
  connectToWebsocketTimeout: any;
  notificationsSubscription: any;

  user: User;

  constructor(readonly globalStore: GlobalStore,
              private authService: AuthService,
              private http: HttpClient,
              private ngZone: NgZone,
              private targetCompanyService: TargetCompanyService,
              private usersService: UsersService) {
    this.globalStore.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      this.user = user;

      if (user) {
        this.ensureWebsocketConnection();
        return;
      }

      this.notificationsList = [];
      this.unreadNotificationsList = [];
      this.unreadNotifCount = 0;
      this.notificationsChanged$.next();
    });

    this.globalStore.companyId$.pipe(takeUntil(this.destroy$)).subscribe((companyId) => {
      this.currentCompanyId = companyId ?? this.resolveCompanyId();
      this.resubscribeToNotifications();
    });
  }

  errorNotification(msg: string) {
    this.showNotification.emit({msg: msg, type: 'error'});
  }

  successNotification(msg: string = CHANGES_SAVED_MESSAGE) {
    this.showNotification.emit({msg: msg, type: 'success'});
  }


  getNotifications(lastNotifId?: number | null) {
    let params = new HttpParams();
    if (lastNotifId) {
      params = params.append('lastNotifId', lastNotifId);
    }

    return this.http.get<NotificationModel[]>(`${apiUrl}/notifications`, {
      params,
      headers: this.getNotificationHeaders(),
      withCredentials: true
    });
  }

  refreshNotifications() {
    return this.getNotifications().pipe(takeUntil(this.destroy$));
  }

  replaceNotificationsList(notifications: NotificationModel[]) {
    this.notificationsList = [...notifications].sort((left, right) => right.createdAt - left.createdAt);
    this.rebuildUnreadNotificationsList();
    this.notificationsChanged$.next();
  }

  onConnected = (userId: number) => {
    this.isConnectingWebsocket = false;
    this.reconnectAttempt = 0;
    this.newConnectionStatus.emit(true);
    if (this.connectToWebsocketTimeout) clearTimeout(this.connectToWebsocketTimeout)

    this.subscribeToNotifications(userId);
    this.stompClient.subscribe("/user/" + userId + "/forceLogout", this.onForceLogout.bind(this));
    this.stompClient.subscribe("/user/" + userId + "/queue/pong", this.onPong.bind(this));
  }

  private subscribeToNotifications(userId: number) {
    const companyId = this.currentCompanyId ?? this.resolveCompanyId();
    if (!companyId) return;

    this.notificationsSubscription = this.stompClient.subscribe(
      `/user/${userId}/company/${companyId}/notifications`,
      this.onMessageReceived.bind(this),
      {}
    );
  }

  private resubscribeToNotifications() {
    if (!this.stompClient?.connected || !this.user?.id) return;

    if (this.notificationsSubscription) {
      this.notificationsSubscription.unsubscribe();
    }

    this.subscribeToNotifications(this.user.id);
  }

  onMessageReceived(msg: Frame) {
    this.ngZone.run(() => {
      const parsed = JSON.parse(msg.body);
      if (Array.isArray(parsed)) {
        this.updateNotificationsList(parsed as NotificationModel[]);
        this.refreshUnreadCount();
        return;
      }

      this.applyRealtimeNotification(parsed as NotificationModel);
    });
  }

  onForceLogout(msg: Frame) {
    this.authService.logout();
  }

  sendPing(userId: number) {
    this.stompClient.send("/app/" + userId + "/ping", {}, "");

    this.pingTimeout = setTimeout(() => {
      this.handlePingTimeout();
    }, 3000);
  }

  handlePingTimeout() {
    this.newConnectionStatus.emit(false);
    this.reconnect();
  }

  reconnect() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
    }
    this.reconnectAttempt++;

    if (this.connectToWebsocketTimeout) clearTimeout(this.connectToWebsocketTimeout)
    this.connectToWebsocketTimeout = setTimeout(() => {
      this.connectToWebsocket();
    }, this.reconnectDelay * 1000);
  }

  onPong(msg: Frame) {
    if (this.pingTimeout) clearTimeout(this.pingTimeout);
  }

  notificationDelivered(notifId: number): Observable<void> {
    return this.http.put<void>(`${apiUrl}/notifications/${notifId}/delivered`, null, {
      headers: this.getNotificationHeaders(),
      withCredentials: true
    });
  }

  getCountUnreadNotifications(): Observable<number> {
    return this.http.get<number>(`${apiUrl}/notifications/unreadCount`, {
      headers: this.getNotificationHeaders(),
      withCredentials: true
    })
  }

  refreshUnreadCount(onResolved?: (count: number) => void) {
    const requestVersion = ++this.unreadCountRequestVersion;

    this.getCountUnreadNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => {
        if (requestVersion !== this.unreadCountRequestVersion) {
          return;
        }

        this.unreadNotifCount = count;
        onResolved?.(count);
        this.notificationsChanged$.next();
      });
  }

  markNotificationsAsRead(notificationIds: number[]) {
    if (!notificationIds.length) {
      return;
    }

    const deliveredIds = new Set(notificationIds);
    let deliveredUnreadNotificationsCount = 0;

    this.notificationsList = this.notificationsList.map((notification) => {
      if (!deliveredIds.has(notification.id) || notification.delivered) {
        return notification;
      }

      deliveredUnreadNotificationsCount += 1;
      return {
        ...notification,
        delivered: true,
        wasRead: true
      };
    });

    this.rebuildUnreadNotificationsList();
    this.unreadNotifCount = Math.max(this.unreadNotifCount - deliveredUnreadNotificationsCount, 0);
    this.notificationsChanged$.next();
  }

  connectToWebsocket() {
    if (!this.user || this.stompClient?.connected || this.isConnectingWebsocket) return;

    this.isConnectingWebsocket = true;
    this.socket = new SockJS(webSocketUrl);
    this.stompClient = Stomp.over(this.socket);
    this.stompClient.debug = null;
    this.stompClient.connect({}, this.onConnected.bind(this, this.user.id), this.onError.bind(this));
  }

  private ensureWebsocketConnection() {
    if (!this.user?.id) return;

    if (this.stompClient?.connected) {
      this.resubscribeToNotifications();
      return;
    }

    this.connectToWebsocket();
  }

  private resolveCompanyId(): number | null {
    return this.currentCompanyId
      ?? this.targetCompanyService.getTargetCompanyId(
        this.usersService.getPrimaryCompanyId(this.user, this.currentCompanyId),
        window.location.href
      );
  }

  private getNotificationHeaders(): HttpHeaders {
    const companyId = this.resolveCompanyId();
    return companyId
      ? new HttpHeaders({'X-Company-Id': String(companyId)})
      : new HttpHeaders();
  }

  private updateNotificationsList(notifications: NotificationModel[]) {
    const notificationsById = new Map<number, NotificationModel>();

    [...notifications, ...this.notificationsList].forEach((notification) => {
      notificationsById.set(notification.id, notification);
    });

    this.notificationsList = [...notificationsById.values()]
      .sort((left, right) => right.createdAt - left.createdAt);
    this.rebuildUnreadNotificationsList();
    this.notificationsChanged$.next();
  }

  private applyRealtimeNotification(notification: NotificationModel) {
    const previousNotification = this.notificationsList.find((item) => item.id === notification.id);
    const wasPreviouslyUnread = previousNotification ? !previousNotification.delivered : false;
    const isUnreadNow = !notification.delivered;

    this.updateNotificationsList([notification]);

    if (!previousNotification && isUnreadNow) {
      this.unreadNotifCount += 1;
      this.notificationsChanged$.next();
      return;
    }

    if (previousNotification && wasPreviouslyUnread !== isUnreadNow) {
      this.unreadNotifCount = Math.max(
        this.unreadNotifCount + (isUnreadNow ? 1 : -1),
        0
      );
      this.notificationsChanged$.next();
    }
  }

  private rebuildUnreadNotificationsList() {
    this.unreadNotificationsList = this.notificationsList.filter(notif => notif.delivered === false);
  }

  onError(error: any) {
    this.isConnectingWebsocket = false;
    console.error('WebSocket error occurred', error);
    this.reconnect()
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
